import * as React from "react";

import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check, Minus } from "lucide-react";

import { cn } from "../../utils";

type CheckboxProps = Omit<
  React.ComponentProps<typeof CheckboxPrimitive.Root>,
  "onCheckedChange"
> & {
  onCheckedChange?: (checked: CheckboxPrimitive.CheckedState) => void;
};

const Checkbox = ({
  className,
  defaultChecked,
  checked,
  ref,
  onCheckedChange,
  ...props
}: CheckboxProps) => {
  const [internalChecked, setInternalChecked] = React.useState(defaultChecked);
  const isControlled = checked !== undefined;
  const checkedValue = isControlled ? checked : internalChecked;

  const isChecked = checkedValue === true;
  const isIndeterminate = checkedValue === "indeterminate";

  const handleCheckedChange = React.useCallback(
    (newChecked: CheckboxPrimitive.CheckedState) => {
      if (!isControlled) setInternalChecked(newChecked);
      onCheckedChange?.(newChecked);
    },
    [isControlled, onCheckedChange],
  );

  return (
    <CheckboxPrimitive.Root
      ref={ref}
      className={cn(
        "af-peer af-h-4 af-w-4 af-shrink-0 af-rounded-sm af-border af-border-primary af-text-primary-foreground af-ring-offset-background focus-visible:af-outline-none focus-visible:af-ring-2 focus-visible:af-ring-ring focus-visible:af-ring-offset-2 disabled:af-cursor-not-allowed disabled:af-opacity-50 data-[state=indeterminate]:af-border-primary data-[state=checked]:af-bg-primary data-[state=indeterminate]:af-bg-primary",
        className,
      )}
      checked={checkedValue}
      onCheckedChange={handleCheckedChange}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        className={cn(
          "af-flex af-items-center af-justify-center af-text-current",
        )}
      >
        {isChecked && <Check className="af-h-3.5 af-w-3.5" />}
        {isIndeterminate && <Minus className="af-h-3.5 af-w-3.5" />}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
};
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
